import React, { useState, useEffect, useRef } from 'react';
import Modal from 'antd/lib/modal';
import Button from 'antd/lib/button';
import Spin from 'antd/lib/spin';
import Form from 'antd/lib/form';
import Input from 'antd/lib/input';
import Menu from 'antd/lib/menu';
import Dropdown from 'antd/lib/dropdown';
import Checkbox from 'antd/lib/checkbox';
import { verifyBuildingCode, registerBuildingUser } from 'parity-network-requests';
import { config } from '../../../config';

// ICONS
import dropdownArrowGrey from '../../../icons/dropdown-grey.svg';
import dropdownArrowLightGrey from '../../../icons/dropdown-light-grey.svg';
import dropdownArrowBlue from '../../../icons/dropdown-blue.svg';

// IMAGES
import addingBuildingLogo from '../../../images/adding-buildings.png';
import buildingSuccess from '../../../images/add-building-success.png';

import './AddBuildingModal.less';

const { REACT_APP_BUILD_ENV } = config;

export const AddBuildingModal = (props) => {
  const { onCancel, theme } = props;
  const [isLoading, setIsLoading] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [buildingKey, setBuildingKey] = useState('');
  const [userRole, setUserRole] = useState('');
  const [isReceivingAlerts, setIsReceivingAlerts] = useState(true);
  const [buildingVerified, setBuildingVerified] = useState(false);
  const [addBuildingError, setAddBuildingError] = useState('');
  const [buildingUserAdded, setBuildingUserAdded] = useState(false);
  const [buildingName, setBuildingName] = useState('');
  const buildingCodeRef = useRef(null);

  const userRoleOptions = [
    { value: 'assistant_property_manager', label: 'Assistant Property Manager' },
    { value: 'board_member', label: 'Board member' },
    { value: 'concierge', label: 'Concierge' },
    { value: 'energy_manager', label: 'Energy Manager' },
    { value: 'external_consultant', label: 'External Consultant' },
    { value: 'engineer', label: 'Engineer' },
    { value: 'executive', label: 'Executive' },
    { value: 'installer', label: 'Installer' },
    { value: 'mechanical', label: 'Mechanical Contractor' },
    { value: 'project_manager', label: 'Project Manager' },
    { value: 'property_manager', label: 'Property Manager' },
    { value: 'superintendent', label: 'Superintendent' },
  ];

  useEffect(() => {
    buildingCodeRef.current.focus();
  }, []);

  const resetAddBuilding = () => {
    setBuildingKey('');
    setUserRole('');
    setIsReceivingAlerts(true);
    setBuildingVerified(false);
    setAddBuildingError('');
    setBuildingUserAdded(false);
  };

  const submitBuildingKey = async () => {
    setIsLoading(true);
    const response = await verifyBuildingCode(REACT_APP_BUILD_ENV, buildingKey);
    if (typeof response === 'string') {
      setAddBuildingError('This building does not exist, please double check your code.');
    } else if (response.building_user_exists) {
      setAddBuildingError('You have already added building. Try closing this window and then refreshing page');
    } else {
      setAddBuildingError('');
      setBuildingVerified(true);
      setBuildingName(response.remote_building.name);
    }
    setIsLoading(false);
  };

  const submitRegisterBuildingUser = async () => {
    setIsLoading(true);
    const response = await registerBuildingUser(REACT_APP_BUILD_ENV, buildingKey, userRole.value, isReceivingAlerts);

    if (response === 201) {
      setBuildingUserAdded(true);
    } else {
      setAddBuildingError(response);
    }
    setIsLoading(false);
  };

  const getDropdownIcon = () => {
    if (isDropdownOpen) {
      return <img className="dropdown-icon" src={dropdownArrowBlue} alt="dropdown" />;
    }
    return theme === 'light' ? (
      <img className="dropdown-icon" src={dropdownArrowGrey} alt="dropdown" />
    ) : (
      <img className="dropdown-icon" src={dropdownArrowLightGrey} alt="dropdown" />
    );
  };

  const renderSwitch = () => {
    if (buildingUserAdded) {
      return (
        <>
          <div className="add-building-modal-success">
            <div className="add-building-modal-success-content">
              <img className="building-success-logo" src={buildingSuccess} alt="success building logo" />
              <div className="add-building-modal-content-text-success">Building successfully added!</div>
              <div className="add-building-modal-content-text-success-description">
                Now you can monitor and see how much Parity is saving this building
              </div>
              <div className="add-building-form__button-wrapper-add">
                <Button
                  size="large"
                  className="add-building-form__button-add"
                  onClick={() => {
                    resetAddBuilding();
                  }}
                >
                  <div className="add-building-form__button-text-add">Add more buildings</div>
                </Button>
              </div>
              <div className="add-building-form__button-wrapper-done">
                <Button block size="large" onClick={() => onCancel()}>
                  Done
                </Button>
              </div>
            </div>
          </div>
        </>
      );
    }

    if (buildingVerified) {
      return (
        <>
          <img src={addingBuildingLogo} className="add-building-logo" alt="building logo" />
          <div className="add-building-modal-content-text-building">This building key is associated with</div>
          <div className="add-building-modal-content-text-building-name">{buildingName}</div>
          {addBuildingError && <div className="add-building-modal-content-error">{addBuildingError}</div>}
          <Form
            layout="vertical"
            className={`add-building-form app-form-${theme}`}
            onFinish={() => submitRegisterBuildingUser()}
          >
            <Form.Item
              name="user_role"
              label="Select your role for this building:"
              className="add-building-form__input-wrapper"
            >
              <Dropdown
                className={`app-dropdown app-dropdown-${theme}`}
                trigger={['click']}
                onVisibleChange={() => setIsDropdownOpen(!isDropdownOpen)}
                overlay={
                  <Menu className={`add-building-dropdown-menu add-building-dropdown-menu-${theme}`}>
                    {userRoleOptions.map((role) => {
                      const { value, label } = role;

                      return (
                        <Menu.Item key={value} onClick={() => setUserRole(role)}>
                          {label}
                        </Menu.Item>
                      );
                    })}
                  </Menu>
                }
              >
                <div className="add-building-form__role-select">
                  {userRole.label}
                  {getDropdownIcon()}
                </div>
              </Dropdown>
            </Form.Item>
            <Form.Item name="isReceivingAlerts" valuePropName="checked">
              <Checkbox
                className={`app-checkbox-${theme}`}
                checked={isReceivingAlerts}
                onChange={(e) => setIsReceivingAlerts(e.target.checked)}
              >
                Subscribe me to alerts so I can be notified with what&apos;s happening with this building
              </Checkbox>
            </Form.Item>
            <div className="add-building-form__button-wrapper">
              <Button block htmlType="submit" size="large" className="add-building-form__button">
                <div className="add-building-form__button-text">Add</div>
              </Button>
            </div>
          </Form>
        </>
      );
    }

    return (
      <>
        <div className="add-building-modal-content-text">
          Please enter a building key provided by Parity. If you don&apos;t have one, please contact us.
        </div>
        {addBuildingError && <div className="add-building-modal-content-error">{addBuildingError}</div>}
        <Form layout="vertical" className={`add-building-form app-form-${theme}`} onFinish={() => submitBuildingKey()}>
          <Form.Item
            name="buildingkey"
            label="Enter Building Key:"
            className="add-building-form__input-wrapper"
            rules={[
              {
                required: true,
                message: 'Please enter your building key.',
              },
            ]}
          >
            <Input
              value={buildingKey}
              onChange={(e) => setBuildingKey(e.target.value)}
              size="large"
              className="add-building-form__input"
              ref={buildingCodeRef}
            />
          </Form.Item>
          <div className="add-building-form__button-wrapper">
            <Button block htmlType="submit" size="large" className="add-building-form__button">
              <div className="add-building-form__button-text">Next</div>
            </Button>
          </div>
        </Form>
      </>
    );
  };

  return (
    <Modal
      className={`add-building-modal add-building-modal-${theme}`}
      centered
      visible
      keyboard
      title="Add Building"
      onCancel={onCancel}
      footer={null}
    >
      {isLoading ? (
        <div className="add-building-modal-content--loading">
          <Spin size="large" />
        </div>
      ) : (
        <div className="add-building-modal-content">{renderSwitch()}</div>
      )}
    </Modal>
  );
};
